import * as React from 'react'
import { Input } from '@/components/ui/Input'

interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** input の id。label の htmlFor と紐付ける。 */
  id: string
  /** ラベル文言。 */
  label: string
  /** バリデーションエラー文言。表示時に aria-invalid / aria-describedby を付与。 */
  error?: string
}

/**
 * ラベル付き入力フィールド (v2)。
 *
 * - label と input を htmlFor / id で関連付け (getByLabelText で取得可能)。
 * - error がある場合は `text-v2-danger` で表示し aria-invalid を立てる。
 * - WCAG: label 必須、focus ring は v2 Input が担保。
 */
export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  ({ id, label, error, ...inputProps }, ref) => {
    const errorId = error ? `${id}-error` : undefined
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={id} className="text-sm font-medium text-v2-ink">
          {label}
        </label>
        <Input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          {...inputProps}
        />
        {error ? (
          <p id={errorId} role="alert" className="text-sm text-v2-danger">
            {error}
          </p>
        ) : null}
      </div>
    )
  },
)
FormField.displayName = 'FormField'

/** 「または」区切り線。 */
export function OrDivider({ label = 'または' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3" role="separator" aria-label={label}>
      <span className="h-px flex-1 bg-v2-border" />
      <span className="text-sm text-v2-ink-soft">{label}</span>
      <span className="h-px flex-1 bg-v2-border" />
    </div>
  )
}
